// Location: src/features/labels/DeviceLabelsCard.tsx
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
  Select,
  CircularProgress,
  type SelectChangeEvent,
} from '@mui/material';
import LabelIcon from '@mui/icons-material/LocalOffer';
import { useDataProvider, useNotify, useTranslate, useRecordContext } from 'react-admin';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { getCurrentUser } from '@/utils/auth';
import type { Device } from '@/types/models';
import type { CatalogLabel, LabelScope } from './types';
import { LabelChip } from './LabelChip';

const FREE = '__free__';

/**
 * Assign / remove curated labels on a device. Changes are applied immediately
 * (independent of the surrounding form submit). Scoped values can be created
 * inline (reuse-first via autocomplete); free labels are picked from the catalog.
 */
export const DeviceLabelsCard = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider() as any;
  const notify = useNotify();
  const record = useRecordContext<Device>();
  const targetId = record?.installationCode;

  const [assigned, setAssigned] = useState<CatalogLabel[]>([]);
  const [scopes, setScopes] = useState<LabelScope[]>([]);
  const [scopeId, setScopeId] = useState<string>('');
  const [options, setOptions] = useState<CatalogLabel[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const user = getCurrentUser();
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';

  const loadAssigned = async () => {
    if (!targetId) return;
    const { data } = await dataProvider.getTargetLabels('device', targetId);
    setAssigned(data);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [scopesRes] = await Promise.all([dataProvider.getLabelScopes()]);
        if (!active) return;
        setScopes(scopesRes.data);
        await loadAssigned();
      } catch {
        if (active) notify('resources.labels.errors.loadFailed', { type: 'warning' });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId]);

  // Reload the value options whenever the selected scope changes.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await dataProvider.getLabels(
          scopeId && scopeId !== FREE ? { scopeId } : {}
        );
        if (!active) return;
        // For the free option, keep only scope-less labels.
        setOptions(scopeId === FREE ? data.filter((l: CatalogLabel) => !l.scopeId) : data);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId]);

  const selectedScope = useMemo(() => scopes.find((s) => s.id === scopeId), [scopes, scopeId]);
  const canCreateInline = scopeId && scopeId !== FREE
    ? true // scoped value: owner inherited from scope
    : isSuperAdmin || !!record?.groupId; // free value: needs an owner we can resolve

  const assign = async (labelId: string) => {
    if (!targetId) return;
    setBusy(true);
    try {
      await dataProvider.assignLabel({ labelId, targetType: 'device', targetId });
      await loadAssigned();
      setInputValue('');
    } catch (e: any) {
      notify(e?.body?.error?.message || 'resources.labels.errors.assignFailed', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const createAndAssign = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || !targetId) return;
    setBusy(true);
    try {
      const input: Record<string, unknown> =
        scopeId && scopeId !== FREE
          ? { scopeId, value: trimmed }
          : {
              value: trimmed,
              ownerType: isSuperAdmin ? 'global' : 'group',
              ownerGroupId: isSuperAdmin ? undefined : record?.groupId,
            };
      const { data } = await dataProvider.createLabel(input);
      await dataProvider.assignLabel({ labelId: data.id, targetType: 'device', targetId });
      await loadAssigned();
      setInputValue('');
    } catch (e: any) {
      notify(e?.body?.error?.message || 'resources.labels.errors.createFailed', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (labelId: string) => {
    if (!targetId) return;
    setBusy(true);
    try {
      await dataProvider.unassignLabel('device', targetId, labelId);
      await loadAssigned();
    } catch {
      notify('resources.labels.errors.removeFailed', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (!record) return null;

  // Don't offer already-assigned values.
  const assignedIds = new Set(assigned.map((a) => a.id));
  const freeOptions = options.filter((o) => !assignedIds.has(o.id));

  return (
    <MyFormSection
      title={translate('resources.labels.deviceCard.title')}
      subtitle={translate('resources.labels.deviceCard.subtitle')}
      icon={<LabelIcon />}
      variant="outlined"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Assigned labels */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, minHeight: 24 }}>
            {assigned.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {translate('resources.labels.deviceCard.empty')}
              </Typography>
            ) : (
              assigned.map((l) => (
                <LabelChip
                  key={l.id}
                  scope={l.scope}
                  value={l.value}
                  color={l.color}
                  size="medium"
                  onDelete={busy ? undefined : () => remove(l.id)}
                />
              ))
            )}
          </Box>

          {/* Add control: scope + value */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Select
              size="small"
              displayEmpty
              value={scopeId}
              onChange={(e: SelectChangeEvent) => {
                setScopeId(e.target.value);
                setInputValue('');
              }}
              sx={{ minWidth: 150 }}
              renderValue={(v) => {
                if (!v) return translate('resources.labels.deviceCard.selectScope');
                if (v === FREE) return translate('resources.labels.deviceCard.freeLabel');
                const s = scopes.find((x) => x.id === v);
                return s?.displayName || s?.key || '';
              }}
            >
              <MenuItem value={FREE}>{translate('resources.labels.deviceCard.freeLabel')}</MenuItem>
              {scopes.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.displayName || s.key}
                </MenuItem>
              ))}
            </Select>

            <Autocomplete
              size="small"
              freeSolo={canCreateInline}
              disabled={busy}
              sx={{ flex: 1, minWidth: 200 }}
              options={freeOptions}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.value)}
              inputValue={inputValue}
              onInputChange={(_, v) => setInputValue(v)}
              filterOptions={(opts, state) => {
                const q = state.inputValue.trim().toLowerCase();
                return q ? opts.filter((o) => o.value.toLowerCase().includes(q)) : opts;
              }}
              onChange={(_, val) => {
                if (!val) return;
                if (typeof val === 'string') createAndAssign(val);
                else assign(val.id);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate('resources.labels.deviceCard.valuePlaceholder')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canCreateInline && inputValue.trim()) {
                      const exact = freeOptions.find(
                        (o) => o.value.toLowerCase() === inputValue.trim().toLowerCase()
                      );
                      e.preventDefault();
                      if (exact) assign(exact.id);
                      else createAndAssign(inputValue);
                    }
                  }}
                />
              )}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {translate('resources.labels.deviceCard.hint')}
          </Typography>
        </Box>
      )}
    </MyFormSection>
  );
};

export default DeviceLabelsCard;
